import React, { useState, useRef, useEffect } from "react";

/**
 * HowItWorksHover - mostra un piccolo link "Come funziona?" che,
 * al passaggio del mouse (o tap su mobile), apre un tooltip semitrasparente
 * con i passi del flow. Variante: "client" o "business".
 */
const CLIENT_STEPS = [
  { n: 1, title: "Cerca", text: "Filtra le piattaforme aeree per disponibilità, città e tipo di noleggio." },
  { n: 2, title: "Confronta", text: "Vedi prezzi, posizione e recensioni delle aziende." },
  { n: 3, title: "Noleggia", text: "Clicca \"Contatta ora\" per poter noleggiare la tua Piattaforma aerea parlando direttamente con il noleggiante." },
];

const BUSINESS_STEPS = [
  { n: 1, title: "Registra l'azienda", text: "Registrati con i tuoi dati aziendali." },
  { n: 2, title: "Registra la tua prima Piattaforma Aerea", text: "Registra la tua prima piattaforma aerea e caricane nuove nella tua area personale." },
  { n: 3, title: "Ricevi richieste", text: "I clienti ti contatteranno telefonicamente dopo aver visto i tuoi annunci." },
];

const HowItWorksHover = ({ variant = "client", className = "" }) => {
  const steps = variant === "business" ? BUSINESS_STEPS : CLIENT_STEPS;
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  // Close on outside click (mobile tap) - use mousedown to fire BEFORE the click toggle
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest("[data-howit]")) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <span className={`relative inline-block ${className}`} data-howit data-testid={`how-it-works-${variant}`}>
      <button
        type="button"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        className="text-orange-600 hover:text-orange-700 text-sm font-medium underline underline-offset-2 decoration-dotted cursor-help"
        data-testid={`how-it-works-trigger-${variant}`}
      >
        Come funziona?
      </button>

      {open && (
        <div
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          className="absolute z-[100] right-0 mt-2 w-80 max-w-[90vw] bg-white/95 backdrop-blur-md border border-orange-200 rounded-xl shadow-2xl p-4 animate-in fade-in slide-in-from-top-2 duration-200"
          style={{ top: '100%' }}
          data-testid={`how-it-works-tooltip-${variant}`}
        >
          <p className="text-xs font-semibold text-orange-600 mb-3 uppercase tracking-wide">
            {variant === "business" ? "Per chi noleggia" : "Per chi cerca"}
          </p>
          <ol className="space-y-2.5">
            {steps.map((s) => (
              <li key={s.n} className="flex gap-3 items-start">
                <span className="shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white text-xs font-bold shadow-sm">
                  {s.n}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 leading-tight">{s.title}</div>
                  <div className="text-xs text-slate-600 leading-snug mt-0.5">{s.text}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </span>
  );
};

export default HowItWorksHover;
